/**
 * TargetRegistry — a single per-frame registry R(t) that UNIFIES the outputs
 * of the otherwise-separate detectors (paper homography, tracked desk objects,
 * other persons) into one canonical list of gaze targets.
 *
 * Motivation (2026-07-08, patent-gap work): the engine already has strong
 * individual detectors, but the patent disclosure (§5.9) describes a common
 * target registry that everything is projected onto. Today's detectors are
 * modular and fused only at the scene-rule layer; this module is the assembly
 * step that makes the registry explicit — the input the gaze-likelihood
 * (§5.10) and observation-vector (§6) stages consume.
 *
 * Pure logic (no DOM), additive, unit-testable. It does NOT change any
 * existing detector; it only reads their outputs and produces a new record.
 * All coordinates normalized [0,1].
 */

import type { BBoxN, TrackedObject } from './ObjectTracker';
import type { PaperZone, PartialHomographyKind } from './Homography';

export type TargetType =
  | 'paper_zone' // physical exam sheet / work medium
  | 'physical_object' // tracked desk object (pen/phone/book…)
  | 'person' // another person's face region
  | 'digital_screen'; // placeholder — digital targets not yet implemented

/** Neutral, observation-only role. Never a verdict. */
export type SemanticRole =
  | 'own_exam_sheet'
  | 'permitted_reference'
  | 'prohibited_device_candidate'
  | 'writing_tool'
  | 'other_person'
  | 'unknown';

export interface RegistryTarget {
  targetId: string;
  type: TargetType;
  /** Normalized [0,1] center of the target. */
  center: { x: number; y: number };
  /** Optional normalized bbox (objects/persons). */
  bbox?: BBoxN;
  coordinateSystem: 'image_norm' | 'paper_norm';
  semanticRole: SemanticRole;
  /** Raw object label (physical_object only) — enables permitted-material matching. */
  label?: string;
  /** Persistent source id for object/person tracks, if any. */
  sourceId?: number | string;
  /** Visibility proxy [0,1]; lower when coasting/occluded or low-confidence H. */
  visibility: number;
  /** Track memory state where applicable. */
  memoryState?: 'active' | 'coasting';
  confidence: number;
}

export interface TargetRegistry {
  t: number;
  targets: RegistryTarget[];
}

/** Map a (possibly English/COCO) object label to a neutral semantic role. */
export function roleForLabel(label: string): SemanticRole {
  const l = label.toLowerCase();
  // Own work medium (§5.6): the student's own sheet/scratch/worksheet — looking
  // at these is normal, so they must not fall through to 'unknown' (→ surfaced).
  if (/(answer.?sheet|answer_sheet|scratch|worksheet|work.?sheet|sketchbook|exam.?paper|시험지|답안|연습지)/.test(l)) {
    return 'own_exam_sheet';
  }
  if (/(phone|smartphone|tablet|cell)/.test(l)) return 'prohibited_device_candidate';
  if (/(book|notebook|formula|reference|textbook|교재|책|참고)/.test(l)) return 'permitted_reference';
  if (/(pen|pencil|stylus|eraser|marker|필기|연필|펜)/.test(l)) return 'writing_tool';
  // NOTE: calculator is intentionally left 'unknown' — it is permitted in some
  // exams and not others, so the task's permittedMaterials config decides.
  return 'unknown';
}

function centerOf(b: BBoxN): { x: number; y: number } {
  return { x: b.x + b.w / 2, y: b.y + b.h / 2 };
}

export interface PaperTargetInput {
  /** Normalized center of the paper quad (fallback if no quad supplied). */
  center: { x: number; y: number };
  bbox?: BBoxN;
  /** Which estimator produced the homography — drives visibility. */
  homographyKind?: PartialHomographyKind;
  /** 0..1 confidence proxy from the homography result. */
  homographyConfidence?: number;
  /** Optional current zone of the gaze on the paper (annotation only). */
  zone?: PaperZone;
}

export interface PersonTargetInput {
  id: number | string;
  center: { x: number; y: number };
  bbox?: BBoxN;
}

export interface RegistryInputs {
  t: number;
  paper?: PaperTargetInput | null;
  objects?: TrackedObject[] | null;
  persons?: PersonTargetInput[] | null;
}

const KIND_VIS: Record<PartialHomographyKind, number> = {
  full: 1,
  affine: 0.7,
  none: 0.3,
};

/**
 * Assemble the unified target registry for one frame from whatever detector
 * outputs are available. Missing inputs are simply skipped — the registry is
 * always well-formed, possibly empty.
 */
export function buildTargetRegistry(inp: RegistryInputs): TargetRegistry {
  const targets: RegistryTarget[] = [];

  if (inp.paper) {
    const p = inp.paper;
    const kind = p.homographyKind ?? 'none';
    const vis = p.homographyConfidence ?? KIND_VIS[kind];
    targets.push({
      targetId: 'paper:own',
      type: 'paper_zone',
      center: p.center,
      bbox: p.bbox,
      coordinateSystem: 'paper_norm',
      semanticRole: 'own_exam_sheet',
      visibility: clamp01(vis),
      confidence: clamp01(vis),
    });
  }

  for (const o of inp.objects ?? []) {
    const vis = o.state === 'active' ? 1 : 0.4;
    targets.push({
      targetId: `obj:${o.id}`,
      type: 'physical_object',
      center: centerOf(o.bbox),
      bbox: o.bbox,
      coordinateSystem: 'image_norm',
      semanticRole: roleForLabel(o.label),
      label: o.label,
      sourceId: o.id,
      visibility: vis,
      memoryState: o.state,
      confidence: clamp01(o.score),
    });
  }

  for (const person of inp.persons ?? []) {
    targets.push({
      targetId: `person:${person.id}`,
      type: 'person',
      center: person.center,
      bbox: person.bbox,
      coordinateSystem: 'image_norm',
      semanticRole: 'other_person',
      sourceId: person.id,
      visibility: 1,
      confidence: 0.9,
    });
  }

  return { t: inp.t, targets };
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
